import { DefaultOptionType } from 'antd/es/cascader'
import { NormalizedBuilderNode, NodesById } from '../../../types'
import { Dimension } from '../../../../../../../../../features/dimension/types/Dimension'
import { Company } from '../../../../../../../../../types/company/Company'
import { defaultTitle, transform } from '../../../../../../../../../redux/context/customReports/transUtils'
import { enhanceNodeWithData } from './enhanceNodeWithData'

interface GetNodeLabelDependencies {
  nodesById: NodesById
  dimensionTrees: { [companyId: string]: Dimension[] | undefined }
  companies: Company[]
  primaryCompanyId?: string
  categoryTree: any[]
}

/**
 * Get a human-readable label for a node
 */
export const getNodeLabel = (node: NormalizedBuilderNode, deps: GetNodeLabelDependencies): string => {
  const { nodesById, dimensionTrees, companies, primaryCompanyId, categoryTree } = deps
  const enhancedNode = enhanceNodeWithData(node, nodesById, dimensionTrees, companies, primaryCompanyId)
  try {
    const result = transform(
      enhancedNode.title ?? defaultTitle(enhancedNode.type, enhancedNode.value),
      enhancedNode as any,
      categoryTree,
      true
    )
    return result || String(node.value) || node.type
  } catch {
    return String(node.value) || node.type
  }
}

/**
 * Build a hierarchical cascader tree from paths
 */
export const buildCascaderTree = (
  pathsList: NormalizedBuilderNode[][],
  nodesById: NodesById,
  getLabel: (node: NormalizedBuilderNode) => string
): DefaultOptionType[] => {
  const root: DefaultOptionType[] = []

  // Collect all valid reference target IDs (last node of each path)
  const referenceableIds = new Set(pathsList.filter(p => p.length > 0).map(p => p[p.length - 1].id))

  for (const path of pathsList) {
    // Skip root node
    const pathWithoutRoot = [...path].slice(1)
    let currentLevel = root

    for (let i = 0; i < pathWithoutRoot.length; i += 1) {
      const n = pathWithoutRoot[i]
      const isLast = i === pathWithoutRoot.length - 1
      const isReferenceable = referenceableIds.has(n.id)

      // Get label for this node
      let label: string
      if (n.type === 'reference' && n.value) {
        // Find the path that ends with the referenced target
        const targetPath = pathsList.find(p => p[p.length - 1].id === n.value)
        if (targetPath) {
          const targetPathWithoutRoot = [...targetPath].slice(1)
          const targetPathLabels = targetPathWithoutRoot
            .filter(targetNode => targetNode.type !== 'reference')
            .map(getLabel)
          label = targetPathLabels.join(' / ')
        } else {
          const targetNode = nodesById[n.value as string]
          label = targetNode ? getLabel(targetNode) : String(n.value)
        }
      } else {
        label = getLabel(n)
      }

      // Find existing node at this level
      let existingNode = currentLevel.find(opt => opt.value === n.id)

      if (!existingNode) {
        existingNode = {
          value: n.id,
          label,
          children: isLast ? undefined : [],
          isReferenceable // Mark if this node can be selected as reference target
        }
        currentLevel.push(existingNode)
      } else {
        // Node exists - update isReferenceable if this node is a valid target
        if (isReferenceable) {
          existingNode.isReferenceable = true
        }
        if (!isLast && !existingNode.children) {
          // Node needs children array
          existingNode.children = []
        }
      }

      // Move to next level if not last
      if (!isLast && existingNode.children) {
        currentLevel = existingNode.children
      }
    }
  }

  return root
}

/**
 * Find the full path for a given reference value
 */
export const findCurrentValuePath = (
  value: string | string[] | number | number[] | undefined,
  paths: NormalizedBuilderNode[][]
): string[] | undefined => {
  const normalizedValue = Array.isArray(value) ? value[value.length - 1] : value
  if (normalizedValue == null) return undefined
  const matchingPath = paths.find(p => p.length > 0 && String(p[p.length - 1].id) === String(normalizedValue))

  if (matchingPath) {
    // Return array of IDs without root
    return matchingPath.slice(1).map(n => n.id)
  }
  return undefined
}
